import React, { useEffect, useState, useMemo, useContext } from "react";
import { useNavigate } from "react-router-dom";
import H5 from "components/typography/H5";
import { Box, Divider, Flex, Grid, Label, Link, Spinner, Badge, Button, Image } from "theme-ui";
import { themevals } from "theme/themevals";
import BasicButton from "components/forms/BasicButton";
import { PlusIcon } from "components/svg";
import AnnouncementHistory from "components/Announcement/AnnouncementHistory";
import { Modal, ModalBody, ModalHeader, ModalFooter } from "components/ui/Modal";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import TextInput from "components/forms/TextInput";
import { UsersIcon, CheckMarkIcon, AvatarIcon, QuestionMarkIcon } from "components/svg";
import { createProviderUser, getProviderUsers } from "api/Providers";
import { updateUser, deleteUser, restoreUser, resendInvite } from "api/Users";
import { useToast } from "components/toast";
import TableData from "components/ui/TableData";
import { createColumnHelper } from "@tanstack/react-table";
import { PlaneIcon } from "components/svg";
import { InviteWrapper, EllipsisWrapper } from "./styled";
import moment from "moment-timezone";
import Span from "components/typography/Span";
import UserContext from "context/User";
import DropDownMenu from "components/forms/DropDown/DropDownMenu";
import EllipsisIcon from "components/svg/icons/EllipsisIcon";
import TrashIcon from "components/svg/icons/TrashIcon";
import BasicModal from "components/ui/BasicModal";
import P from "components/typography/P";

type UserRow = {
  firstName?: string;
  status?: number;
};

const ProviderUsers = ({ provider }: { provider: any }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const toast = useToast();
  const columnHelper = createColumnHelper<UserRow>();
  const navigate = useNavigate();
  const { user } = useContext<any>(UserContext);

  // Edit modal state
  const [editModal, setEditModal] = useState(false);
  const [editUser, setEditUser] = useState<any>(null);

  // Confirm modal state (deactivate/reactivate)
  const [confirmModal, setConfirmModal] = useState<React.ReactNode>(null);
  const [confirmSubmit, setConfirmSubmit] = useState(false);

  const CreateSchema = Yup.object().shape({
    firstName: Yup.string().required("This field is required."),
    lastName: Yup.string().required("This field is required."),
    contactEmail: Yup.string().email("Invalid email").required("This field is required."),
  });

  const EditSchema = Yup.object().shape({
    firstName: Yup.string().required("This field is required."),
    lastName: Yup.string().required("This field is required."),
    email: Yup.string().email("Invalid email").required("This field is required."),
  });

  const formatDate = (dateString: string): string => {
    return moment(dateString).format("MMM D, YYYY");
  };

  const triggerRefresh = () => {
    setRefreshData(true);
    setTimeout(() => setRefreshData(false), 100);
  };

  const submitCreate = async (values: any) => {
    const data = {
      firstName: values.firstName,
      lastName: values.lastName,
      contactEmail: values.contactEmail,
      provider: provider.id,
    };
    const res = await createProviderUser({ data });
    if (res?.success) {
      setIsOpen(false);
      triggerRefresh();
      toast.add(res?.message, "var(--theme-ui-colors-success)");
    } else {
      toast.add(res?.message || "An error occurred while creating the user.", "var(--theme-ui-colors-danger)");
    }
  };

  // --- Edit flow ---
  const openEditModal = (userData: any) => {
    setEditUser(userData);
    setEditModal(true);
  };

  const closeEditModal = () => {
    setEditModal(false);
    setEditUser(null);
  };

  const submitUserUpdate = async (values: any) => {
    if (!editUser?.id) return;
    const res = await updateUser({
      data: { id: editUser.id },
      update: {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
      },
    });
    if (res?.id) {
      toast.add("User updated.", "var(--theme-ui-colors-success)");
      closeEditModal();
      triggerRefresh();
    } else {
      toast.add(res?.message || "Error updating user.", "var(--theme-ui-colors-danger)");
    }
  };

  // --- Deactivate flow ---
  const submitDeactivateUser = (targetUserOverride?: any) => {
    const targetUser = targetUserOverride || editUser;
    if (!targetUser) return;
    closeEditModal();
    setConfirmModal(
      <BasicModal
        title="Deactivate user?"
        cancel_click={() => setConfirmModal(null)}
        cancel_text="No, cancel"
        $submitting={false}
        size="sm"
        icon={<QuestionMarkIcon fill="#818181" height="30px" />}
        actions={
          <BasicButton
            type="button"
            scheme="danger"
            onClick={async () => {
              setConfirmSubmit(true);
              const res = await deleteUser({ data: { id: targetUser.id } });
              if (res?.success) {
                toast.add("User deactivated.", "var(--theme-ui-colors-success)");
                triggerRefresh();
              } else {
                toast.add(res?.message || "Error deactivating user.", "var(--theme-ui-colors-danger)");
              }
              setConfirmSubmit(false);
              setConfirmModal(null);
            }}
            $iconHeight="14px"
            $iconWidth="14px"
            $loadingColor="#fff"
            keepText={true}
          >
            <TrashIcon fill="#fff" />
            Deactivate
          </BasicButton>
        }
      >
        <P lineHeight="22px">
          Are you sure you want to deactivate{" "}
          <strong style={{ color: themevals.colors.black }}>
            {targetUser?.firstName} {targetUser?.lastName}
          </strong>
          ? They will no longer be able to access the provider portal.
        </P>
      </BasicModal>
    );
  };

  // --- Reactivate flow ---
  const submitReactivateUser = (targetUser: any) => {
    setConfirmModal(
      <BasicModal
        title="Reactivate user?"
        cancel_click={() => setConfirmModal(null)}
        cancel_text="No, cancel"
        $submitting={false}
        size="sm"
        icon={<QuestionMarkIcon fill="#818181" height="30px" />}
        actions={
          <BasicButton
            type="button"
            onClick={async () => {
              setConfirmSubmit(true);
              const res = await restoreUser({ id: targetUser.id });
              if (res?.success) {
                toast.add("User reactivated.", "var(--theme-ui-colors-success)");
                triggerRefresh();
              } else {
                toast.add(res?.message || "Error reactivating user.", "var(--theme-ui-colors-danger)");
              }
              setConfirmSubmit(false);
              setConfirmModal(null);
            }}
            $iconHeight="14px"
            $iconWidth="14px"
            $loadingColor="#fff"
            keepText={true}
            styles={{
              backgroundColor: themevals.colors.success,
              borderColor: themevals.colors.success,
              color: "#fff",
            }}
          >
            <CheckMarkIcon fill="#fff" />
            Reactivate
          </BasicButton>
        }
      >
        <P lineHeight="22px">
          Are you sure you want to reactivate{" "}
          <strong style={{ color: themevals.colors.black }}>
            {targetUser?.firstName} {targetUser?.lastName}
          </strong>
          ?
        </P>
      </BasicModal>
    );
  };

  // --- Resend invite ---
  const resendUserInvite = async (targetUser: any) => {
    const res = await resendInvite({ data: targetUser });
    if (res?.success) {
      toast.add("Invite resent.", "var(--theme-ui-colors-success)");
      triggerRefresh();
    } else {
      toast.add(res?.message ? res.message : "Error resending invite.", "var(--theme-ui-colors-danger)");
    }
  };

  const columns = [
    columnHelper.accessor((row: any) => row.name, {
      id: "name",
      cell: (info: any) => {
        const userData = info.row.original?.primaryUser?.data || [];
        const userLogo = userData.find((d: any) => d.key === "logo")?.value;
        const firstName = info.row.original?.firstName || "";
        const lastName = info.row.original?.lastName || "";
        const displayName = `${firstName} ${lastName}`.trim();
        return (
          <Flex
            sx={{ gap: "1rem", alignItems: "center", cursor: "pointer" }}
            onClick={() => openEditModal(info.row.original)}
          >
            {userLogo ? (
              <Image width="40px" height="40px" src={userLogo} sx={{ borderRadius: "6px" }} />
            ) : (
              <AvatarIcon fill={"#BFBFBF"} width={"40px"} />
            )}
            <Box>
              <Span color="#000" fontWeight="500">{displayName}</Span>
              <Span color="var(--theme-ui-colors-base_500)" display="block">{info.row.original?.email}</Span>
            </Box>
          </Flex>
        );
      },
      header: () => <span>User Info</span>,
      enableSorting: false,
      meta: {
        style: { width: "400px", minWidth: "400px" },
      },
    }),
    columnHelper.accessor((row) => row?.status, {
      id: "status",
      cell: (info) => {
        const [inviteText, setInviteText] = React.useState("Invite Sent");
        const [isProcessing, setIsProcessing] = React.useState(false);

        const handleMouseEnter = () => {
          if (!isProcessing) setInviteText("Resend Invite");
        };

        const handleMouseLeave = () => {
          if (!isProcessing) setInviteText("Invite Sent");
        };

        const handleClick = async () => {
          setIsProcessing(true);
          try {
            await resendUserInvite(info.row.original);
          } catch (error) {
            console.error("Failed to resend invite:", error);
          } finally {
            setIsProcessing(false);
          }
        };

        switch (info.getValue()) {
          case 0:
            return (
              <Badge as="span" variant="danger">Deactivated</Badge>
            );
          case 1:
            return (
              <Badge as="span" variant="success">Active</Badge>
            );
          case 2:
            return (
              <InviteWrapper onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onClick={handleClick}>
                <Badge as="span" sx={{ gap: "10px" }} variant="info">
                  <span>{inviteText}</span>
                  {!isProcessing ? <PlaneIcon fill="#104E9A" width="14px" /> : <Spinner sx={{ color: "#104E9A" }} size={14} />}
                </Badge>
              </InviteWrapper>
            );
          default:
            return (
              <Badge as="span" variant="success">Active</Badge>
            );
        }
      },
      header: () => <span>Status</span>,
      meta: {
        style: { width: "300px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.lastActiveAt, {
      id: "lastActiveAt",
      cell: (info: any) => (info.getValue() !== null ? formatDate(info.getValue()) : null),
      header: () => <span>Last Active</span>,
      meta: {
        style: { width: "130px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.createdAt, {
      id: "createdAt",
      cell: (info: any) => (info.getValue() !== null ? formatDate(info.getValue()) : null),
      header: () => <span>Date Added</span>,
      meta: {
        style: { width: "130px" },
      },
    }),
    columnHelper.display({
      id: "actions",
      cell: (info: any) => {
        const row = info.row.original;
        const isDeactivated = row?.status === 0;
        const isPending = row?.status === 2;

        const items = [
          { label: "Edit", onClick: () => openEditModal(row) },
          ...(isPending ? [{ label: "Resend Invite", onClick: () => resendUserInvite(row) }] : []),
          ...(isDeactivated
            ? [{
                label: "Reactivate",
                style: { color: themevals.colors.success },
                onClick: () => submitReactivateUser(row),
              }]
            : [{
                label: "Deactivate",
                style: { color: themevals.colors.danger },
                onClick: () => submitDeactivateUser(row),
              }]),
        ];

        return (
          <EllipsisWrapper>
            <DropDownMenu
              toggleComponent={
                <EllipsisIcon fill={"#818181"} width="20px" height="20px" />
              }
              items={items}
            />
          </EllipsisWrapper>
        );
      },
      header: () => <span></span>,
      meta: {
        style: { width: "10px" },
      },
    }),
  ];

  return (
    <>
      <H5 fontFamily="Rubik" fontSize="16px" marginLeft="0px" marginTop="0px">
        Users
      </H5>
      <Flex sx={{ justifyContent: "flex-end", marginBottom: "20px" }}>
        <BasicButton scheme="secondaryOutline" onClick={() => setIsOpen(!isOpen)}>
          <PlusIcon fill={"#000"} />
          {"Add user"}
        </BasicButton>
      </Flex>
      {provider && (
        <TableData
          api={getProviderUsers}
          apiVariables={{
            data: provider,
            ...(user?.accessLevel === "isProvider"
              ? { filters: { providerId: provider.id } }
              : {}),
          }}
          columns={columns}
          refresh={refreshData}
          isFixed={true}
          enableSearch={false}
          enablePaging={false}
          nothingFoundText="No Users found."
        />
      )}

      {/* Create User Modal */}
      <Modal isOpen={isOpen} toggle={() => setIsOpen(!isOpen)} centered={true} size="sm">
        <Formik initialValues={{ color: "#000" }} onSubmit={submitCreate} validationSchema={CreateSchema}>
          {({ isSubmitting, errors, values, submitCount, setFieldValue }: any) => {
            return (
              <Form>
                <ModalHeader toggle={() => setIsOpen(!isOpen)}>
                  <UsersIcon fill={"#000"} width="18px" height="18px" margin="0 1rem 0 0" />
                  <Box>
                    <Box sx={{ fontSize: "16px", fontWeight: 500, fontFamily: "Rubik", color: "base_800" }}>
                      Add User
                    </Box>
                    <Box sx={{ fontSize: "12px", fontFamily: "Poppins", color: "dkgrey", fontWeight: 400 }}>
                      Add a new user to this provider.
                    </Box>
                  </Box>
                </ModalHeader>
                <ModalBody>
                  <TextInput
                    name="firstName"
                    label="First Name"
                    value={values.firstName}
                    onChange={(val: any) => setFieldValue("firstName", val)}
                    $errors={errors.firstName && submitCount > 0 ? errors.firstName : null}
                  />
                  <TextInput
                    name="lastName"
                    label="Last Name"
                    value={values.lastName}
                    onChange={(val: any) => setFieldValue("lastName", val)}
                    $errors={errors.lastName && submitCount > 0 ? errors.lastName : null}
                  />
                  <TextInput
                    name="contactEmail"
                    label="Email"
                    value={values.contactEmail}
                    onChange={(val: any) => setFieldValue("contactEmail", val)}
                    $errors={errors.contactEmail && submitCount > 0 ? errors.contactEmail : null}
                  />
                </ModalBody>
                <ModalFooter>
                  <BasicButton outline={true} styles={{ margin: ".25rem 10px .25rem 0" }} type="button" onClick={() => setIsOpen(!isOpen)} scheme="secondary">
                    Cancel
                  </BasicButton>
                  <BasicButton styles={{ margin: ".25rem 0 .25rem 0" }} type="submit" $submitting={isSubmitting} $iconHeight="14px" $loadingColor="#fff" keepText={true}>
                    {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                    Save User
                  </BasicButton>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </Modal>

      {/* Edit User Modal */}
      {editModal && editUser && (
        <Modal isOpen={editModal} toggle={closeEditModal} centered={true} size="sm">
          <Formik
            initialValues={{
              firstName: editUser.firstName || "",
              lastName: editUser.lastName || "",
              email: editUser.email || "",
            }}
            onSubmit={submitUserUpdate}
            validationSchema={EditSchema}
            enableReinitialize
          >
            {({ isSubmitting, errors, values, submitCount, setFieldValue }: any) => {
              return (
                <Form noValidate autoComplete="off">
                  <ModalHeader toggle={closeEditModal}>
                    <UsersIcon fill={"#000"} width="18px" height="18px" margin="0 1rem 0 0" />
                    <Box>
                      <Box sx={{ fontSize: "16px", fontWeight: 500, fontFamily: "Rubik", color: "base_800" }}>
                        Edit User
                      </Box>
                      <Box sx={{ fontSize: "12px", fontFamily: "Poppins", color: "dkgrey", fontWeight: 400 }}>
                        Update this provider user&apos;s details.
                      </Box>
                    </Box>
                  </ModalHeader>
                  <ModalBody>
                    <TextInput
                      name="firstName"
                      label="First Name"
                      value={values.firstName}
                      onChange={(val: any) => setFieldValue("firstName", val)}
                      $errors={errors.firstName && submitCount > 0 ? errors.firstName : null}
                    />
                    <TextInput
                      name="lastName"
                      label="Last Name"
                      value={values.lastName}
                      onChange={(val: any) => setFieldValue("lastName", val)}
                      $errors={errors.lastName && submitCount > 0 ? errors.lastName : null}
                    />
                    <TextInput
                      type="email"
                      name="email"
                      label="Email"
                      value={values.email}
                      onChange={(val: any) => setFieldValue("email", val)}
                      $errors={errors.email && submitCount > 0 ? errors.email : null}
                    />
                    <AnnouncementHistory userId={editUser?.id} />
                  </ModalBody>
                  <ModalFooter>
                    <Flex sx={{ display: "flex", flexWrap: "wrap-reverse", gap: "20px", justifyContent: "space-between", width: "100%" }}>
                      <Box>
                        {editUser.status !== 0 && (
                          <BasicButton
                            scheme="dangerSecondary"
                            styles={{ margin: ".25rem 0.625rem .25rem 0" }}
                            type="button"
                            onClick={submitDeactivateUser}
                            $iconHeight="14px"
                            $iconWidth="14px"
                            $loadingColor="#fff"
                          >
                            <TrashIcon fill={"#A60000"} />
                            Deactivate
                          </BasicButton>
                        )}
                      </Box>
                      <Flex sx={{ display: "flex", flexWrap: "wrap-reverse" }}>
                        <BasicButton
                          outline={true}
                          styles={{ margin: ".25rem 20px .25rem 0" }}
                          type="button"
                          onClick={closeEditModal}
                          scheme="secondary"
                        >
                          Cancel
                        </BasicButton>
                        <BasicButton
                          styles={{ margin: ".25rem 0 .25rem 0" }}
                          type="submit"
                          $submitting={isSubmitting}
                          $iconHeight="14px"
                          $loadingColor="#fff"
                          keepText={true}
                        >
                          {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                          Update User
                        </BasicButton>
                      </Flex>
                    </Flex>
                  </ModalFooter>
                </Form>
              );
            }}
          </Formik>
        </Modal>
      )}

      {/* Confirm modals (deactivate/reactivate) */}
      {confirmModal}
    </>
  );
};

export default ProviderUsers;
